import {DragDropModule} from '@angular/cdk/drag-drop';
import {
    CommonModule,
    CurrencyPipe,
    DatePipe,
    NgOptimizedImage,
    registerLocaleData,
    TitleCasePipe
} from '@angular/common';
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import {NgbModule, NgbTooltipModule} from '@ng-bootstrap/ng-bootstrap';
import {TranslateLoader, TranslateModule, TranslatePipe} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {LucideAngularModule} from 'lucide-angular';
import {AutosizeModule} from 'ngx-autosize';
import {NgxColorsModule} from 'ngx-colors';
import {DBConfig, NgxIndexedDBModule} from 'ngx-indexed-db';
import {IConfig, NgxMaskModule} from 'ngx-mask';
import {SocketIoConfig, SocketIoModule} from 'ngx-socket-io';
import {CalendarModule} from 'primeng/calendar';
import {ChartModule} from 'primeng/chart';
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {ComponentsModule} from './components/components.module';
import {CodeInputComponent} from './components/form/inputs/code-input/code-input.component';
import {
    BusinessProcessIndexTabComponent
} from './components/index/business-process-index-tab/business-process-index-tab.component';
import {ContractsIndexTabComponent} from './components/index/contracts-index-tab/contracts-index-tab.component';
import {DocumentsIndexPageComponent} from './components/index/documents-index-page/documents-index-page.component';
import {FinanceIndexTabComponent} from './components/index/finance-index-tab/finance-index-tab.component';
import {IndexComponent} from './components/index/index.component';
import {OfficeIndexTabComponent} from './components/index/office-index-tab/office-index-tab.component';
import {ProductsIndexTabComponent} from './components/index/products-index-tab/products-index-tab.component';
import {TasksIndexTabComponent} from './components/index/tasks-index-tab/tasks-index-tab.component';
import {TaskDashboardsV2Component} from './components/task-dashboards/task-dashboards-v2/task-dashboards-v2.component';
import {ProductsComponent} from './controllers/boxs/products/products.component';
import {StoragesComponent} from './controllers/boxs/storages/storages.component';
import {DocumentCollectionComponent} from './controllers/documents/document-collection/document-collection.component';
import {DocumentDetailComponent} from './controllers/documents/document-detail/document-detail.component';
import {DocumentListComponent} from './controllers/documents/document-list/document-list.component';
import {
    DocumentsAccessSettingsComponent
} from './controllers/documents/documents-access-settings/documents-access-settings.component';
import {RegistryDocumentsComponent} from './controllers/documents/registry-documents/registry-documents.component';
import {CreditComponent} from './controllers/finance/credit/credit.component';
import {
    DoughnutFinanceChartComponent,
} from './controllers/finance/doughnut-finance-chart/doughnut-finance-chart.component';
import {FactoringComponent} from './controllers/finance/factoring/factoring.component';
import {FinanceControlComponent} from './controllers/finance/finance-control/finance-control.component';
import {GarantComponent} from './controllers/finance/garant/garant.component';
import {PersonalFinanceComponent} from './controllers/finance/personal-finance/personal-finance.component';
import {MasterWalletComponent} from './controllers/finance/wallets/master-wallet/master-wallet.component';
import {SlaveWalletComponent} from './controllers/finance/wallets/slave-wallet/slave-wallet.component';
import {
    DepartmentsControlDetailComponent,
} from './controllers/project-control/departments-control-detail/departments-control-detail.component';
import {
    DepartmentsControlListComponent,
} from './controllers/project-control/departments-control-list/departments-control-list.component';
import {
    PersonsControlDetailComponent,
} from './controllers/project-control/persons-control-detail/persons-control-detail.component';
import {
    PersonsControlListComponent,
} from './controllers/project-control/persons-control-list/persons-control-list.component';
import {ProfileControlComponent} from './controllers/project-control/profile-control/profile-control.component';
import {IndexStatisticComponent} from './controllers/statistic/index/index.component';
import {TaskDetailComponent} from './controllers/tasks/task-detail/task-detail.component';
import {TaskAccessComponent} from './controllers/tasks/task-options/task-access/task-access.component';
import {TaskBoardsComponent} from './controllers/tasks/task-options/task-boards/task-boards.component';
import {TaskGlobalComponent} from './controllers/tasks/task-options/task-global/task-global.component';
import {TaskOptionsComponent} from './controllers/tasks/task-options/task-options.component';
import {TaskPropertiesComponent} from './controllers/tasks/task-options/task-properties/task-properties.component';
import {TaskStatusesComponent} from './controllers/tasks/task-options/task-statuses/task-statuses.component';
import {TaskTypesComponent} from './controllers/tasks/task-options/task-types/task-types.component';
import {TasksAllComponent} from './controllers/tasks/tasks-all/tasks-all.component';
import {ContractsCol3Component} from './layouts/contracts-col3/contracts-col3.component';
import {CS_RouteReuseStrategy} from './services/reuse-strategy.service';
import {RouteReuseStrategy} from '@angular/router';
import {AppGraphKonvaModule} from './modules/graph-konva/app.graph-konva.module';
import {AppWidgetModule} from './modules/widgets/app.widgets.module';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {AppMaterialModule} from './app.material.module';
import {provideNativeDateAdapter} from '@angular/material/core';
import {graphStoreConfig} from './modules/graph-konva/models/graph-kn.cm.model-const';
import {MatTooltipModule} from '@angular/material/tooltip';
import {AppGGModule} from './modules/gg-gateway/app.gg.module';
import {AppContractModule} from './modules/contracts/app.contract.module';
import {AppTasksModule} from './controllers/tasks/app.tasks.module';
import {AppLayoutModule} from './modules/layout-global/app.layout.module';
import {MatDialogModule} from '@angular/material/dialog';
import {UISettingsStoreConfig} from './models/index.db..model';
import {MatSelectModule} from '@angular/material/select';

export const options: Partial<IConfig | null> | (() => Partial<IConfig>) = null;

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http);
}

var dbConfig: DBConfig = {
    name: 'db',
    objectStoresMeta: [
        {
            store: 'files',
            storeConfig: {keyPath: 'id', autoIncrement: true},
            storeSchema: [
                {name: 'file_key', keypath: 'file_key', options: {unique: false}},
                {name: 'version', keypath: 'version', options: {unique: true}},
                {name: 'file', keypath: 'file', options: {unique: false}},
            ],
        },
        graphStoreConfig,
        UISettingsStoreConfig
    ],
};

export function setIndexDBversion(val: number) {
    dbConfig.version ? dbConfig = {...dbConfig, version: val} : dbConfig.version = val;
}

registerLocaleData(localeRu);
const socketConfig: SocketIoConfig = {
    url: environment.socketUrl,
    options: {},
};

@NgModule({
    declarations: [
        AppComponent,
        RegistryDocumentsComponent,
        TasksAllComponent,
        TaskDetailComponent,
        PersonsControlListComponent,
        PersonsControlDetailComponent,
        DepartmentsControlDetailComponent,
        DepartmentsControlListComponent,
        ProfileControlComponent,
        DocumentListComponent,
        DocumentCollectionComponent,
        DocumentDetailComponent,
        FinanceControlComponent,
        TaskOptionsComponent,
        TaskGlobalComponent,
        TaskAccessComponent,
        TaskStatusesComponent,
        TaskBoardsComponent,
        TaskTypesComponent,
        IndexStatisticComponent,
        CreditComponent,
        FactoringComponent,
        GarantComponent,
        StoragesComponent,
        ProductsComponent,
        PersonalFinanceComponent,
        SlaveWalletComponent,
        MasterWalletComponent,
        DoughnutFinanceChartComponent,
        TaskPropertiesComponent,
        DocumentsAccessSettingsComponent,
        ContractsCol3Component,
        IndexComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        AppLayoutModule,
        AppGGModule,
        AppRoutingModule,
        ComponentsModule,
        BrowserAnimationsModule,
        NgxMaskModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
            defaultLanguage: 'ru',
        }),
        NgbModule,
        CalendarModule,
        AutosizeModule,
        DragDropModule,
        CKEditorModule,
        // SpinnersAngularModule,
        NgxColorsModule,
        SocketIoModule.forRoot(socketConfig),
        ChartModule,
        NgxIndexedDBModule.forRoot(dbConfig),
        NgbTooltipModule,
        NgOptimizedImage,
        TranslatePipe,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatSelectModule,
        MatButtonToggleModule,
        LucideAngularModule,
        AppMaterialModule,
        AppWidgetModule,
        AppGraphKonvaModule,
        AppContractModule,
        AppTasksModule,
        CodeInputComponent,
        FinanceIndexTabComponent,
        ContractsIndexTabComponent,
        OfficeIndexTabComponent,
        TasksIndexTabComponent,
        DocumentsIndexPageComponent,
        TaskDashboardsV2Component,
        BusinessProcessIndexTabComponent,
        ProductsIndexTabComponent,
    ],
    providers: [
        DatePipe,
        TitleCasePipe,
        CurrencyPipe,
        provideHttpClient(withInterceptorsFromDi()),
        {provide: RouteReuseStrategy, useClass: CS_RouteReuseStrategy},
        provideAnimationsAsync(),
        provideNativeDateAdapter()
    ]
})
export class AppModule {
}
